import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { usePageMeta } from '@/hooks/usePageMeta';
import { newsList } from '@/mocks/news';
import { movieList } from '@/mocks/movies';
import { topicList } from '@/mocks/topics';
import Breadcrumb from '@/components/feature/Breadcrumb';
import NewsListItem from '@/components/feature/NewsListItem';
import EmptyState from '@/components/feature/EmptyState';
import MovieCard from '@/pages/movies/components/MovieCard';
import { formatViews } from '@/utils/format';

export default function SearchPage() {
  const [params] = useSearchParams();
  const q = (params.get('q') ?? '').trim();

  usePageMeta({
    title: q ? `“${q}” 的搜索结果 - 靠逼` : '搜索 - 靠逼',
    description: `在靠逼搜索“${q}”，查看相关的娱乐新闻、影视推荐与热门话题。`,
    keywords: `${q},搜索,靠逼,娱乐资讯`,
  });

  const result = useMemo(() => {
    if (!q) return { news: [], movies: [], topics: [] };
    const key = q.toLowerCase();
    const news = newsList.filter(
      (n) =>
        n.title.toLowerCase().includes(key) ||
        n.excerpt.toLowerCase().includes(key) ||
        n.tags.some((t) => t.toLowerCase().includes(key)) ||
        n.category.toLowerCase().includes(key),
    );
    const movies = movieList.filter(
      (m) => m.title.toLowerCase().includes(key) || m.genre.toLowerCase().includes(key),
    );
    const topics = topicList.filter(
      (t) =>
        t.title.toLowerCase().includes(key) ||
        t.summary.toLowerCase().includes(key) ||
        t.tags.some((tg) => tg.toLowerCase().includes(key)),
    );
    return { news, movies, topics };
  }, [q]);

  const total = result.news.length + result.movies.length + result.topics.length;

  return (
    <div className="w-full">
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-7 md:px-8">
          <Breadcrumb items={[{ label: '搜索结果' }]} />
          <h1 className="mt-4 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
            搜索结果
          </h1>
          {q ? (
            <p className="mt-1 text-sm text-foreground-500">
              关键词“<span className="font-semibold text-primary-600">{q}</span>”，共找到 {total} 条内容
            </p>
          ) : (
            <p className="mt-1 text-sm text-foreground-500">请在顶部搜索框输入关键词后查看结果</p>
          )}
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-8 md:px-8">
        {!q || total === 0 ? (
          <EmptyState
            icon="ri-search-line"
            title={q ? '没有找到相关内容' : '还没有输入搜索关键词'}
            description={
              q ? '换个关键词试试，比如“盛典”“悬疑”“明星动态”。' : '试试搜索“音乐盛典”“悬疑剧”“热门话题”等。'
            }
            actionLabel="返回首页"
            actionTo="/"
          />
        ) : (
          <div className="flex flex-col gap-10">
            {result.news.length > 0 && (
              <section>
                <div className="mb-4 flex items-center gap-2 border-b border-background-200 pb-3">
                  <span className="flex h-6 w-6 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                    <i className="ri-newspaper-line" />
                  </span>
                  <h2 className="font-heading text-lg font-bold text-foreground-950">相关资讯</h2>
                  <span className="text-xs text-foreground-400">{result.news.length} 条</span>
                </div>
                <div className="flex flex-col gap-3 md:gap-4">
                  {result.news.map((item) => (
                    <NewsListItem key={item.id} item={item} />
                  ))}
                </div>
              </section>
            )}

            {result.movies.length > 0 && (
              <section>
                <div className="mb-4 flex items-center gap-2 border-b border-background-200 pb-3">
                  <span className="flex h-6 w-6 items-center justify-center rounded-md bg-accent-100 text-accent-600">
                    <i className="ri-film-line" />
                  </span>
                  <h2 className="font-heading text-lg font-bold text-foreground-950">相关影视</h2>
                  <span className="text-xs text-foreground-400">{result.movies.length} 部</span>
                </div>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                  {result.movies.map((m) => (
                    <MovieCard key={m.id} movie={m} />
                  ))}
                </div>
              </section>
            )}

            {result.topics.length > 0 && (
              <section>
                <div className="mb-4 flex items-center gap-2 border-b border-background-200 pb-3">
                  <span className="flex h-6 w-6 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                    <i className="ri-fire-line" />
                  </span>
                  <h2 className="font-heading text-lg font-bold text-foreground-950">相关话题</h2>
                  <span className="text-xs text-foreground-400">{result.topics.length} 个</span>
                </div>
                <div className="flex flex-col gap-3">
                  {result.topics.map((t) => (
                    <Link
                      key={t.id}
                      to="/hot-topics"
                      className="group flex items-start justify-between gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-accent-300"
                    >
                      <div className="min-w-0">
                        <h3 className="line-clamp-1 font-heading text-base font-bold text-foreground-950 transition-colors group-hover:text-accent-600">
                          {t.title}
                        </h3>
                        <p className="mt-1 line-clamp-1 text-sm text-foreground-500">{t.summary}</p>
                      </div>
                      <span className="flex flex-none items-center gap-1 text-xs text-foreground-400">
                        <i className="ri-fire-line" />
                        {formatViews(t.heat)}
                      </span>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}